import { observable, action, computed, makeObservable } from 'decorators';
import Pica from 'pica';
import api from 'services/api';
import log from 'services/log';
import loadImage from 'blueimp-load-image';
import { centerCrop, makeAspectCrop } from 'react-image-crop';

const pica = new Pica({ features: [ 'js' ] });

const rotationOrientation = [ 1, 8, 3, 6 ];
const defaultMaxOutputSize = 2000;

export default
class ImageUploadStore {
  @observable isLoadingImage = false;
  @observable image = null;
  @observable crop = null;
  @observable isSaving = false;

  @observable originalImage = null;
  @observable rotation = 0;
  @observable isRotating = false;

  constructor(props) {
    makeObservable(this);
    this._loadingId = 0;
    this.setProps(props);
  }

  @computed get canSave() {
    const c = this.crop;
    return !!(c && c.width && c.height) && !this.isRotating;
  }

  setProps({ picaOptions, output, maxAspect, minAspect, maxOutputSize, onImageBlob, onShowEditor }) {
    this._picaOptions = Object.assign({ alpha: true }, picaOptions);
    this._output = output;
    this._maxAspect = maxAspect;
    this._minAspect = minAspect;
    this._maxOutputSize = maxOutputSize || defaultMaxOutputSize;
    this._onImageBlob = onImageBlob;
    this._onShowEditor = onShowEditor;
  }

  dispose() {
    this.cancel();
  }

  @action handleImages(f) {
    this.cancel();
    this._onShowEditor && this._onShowEditor(true);
    this.isLoadingImage = true;
    const loadId = ++this._loadingId;
    return this._loadImage(f[0].file, true)
      .then(action(image => {
        if (loadId !== this._loadingId) {
          if (image) { window.URL.revokeObjectURL(image); }
          return;
        }
        this.crop = null;
        this.image = image;
        this.originalImage = null;
        this.rotation = 0;
        this.isRotating = false;
        this.isSaving = false;
        this.isLoadingImage = false;
        return new Promise((resolve, reject) => {
          this._uploadResolve = resolve;
        });
      }))
      .finally(action(() => {
        this.isLoadingImage = false;
      }));
  }

  @action rotateImage(clockwise) {
    let newRotation = this.rotation + (clockwise ? -1 : 1);
    const max = rotationOrientation.length - 1;
    if (newRotation < 0) { newRotation = max; }
    if (newRotation > max) { newRotation = 0; }

    if (newRotation === 0) {
      // Just go back to the original image
      this.rotation = 0;
      if (this.originalImage) {
        if (this.image) { window.URL.revokeObjectURL(this.image); }
        this.image = this.originalImage;
        this.originalImage = null;
      }
      this.crop = null;
    } else {
      // We have to create a new image with a rotation, based off the original image
      this.isRotating = true;
      this._loadImage(this.originalImage || this.image, rotationOrientation[newRotation])
        .then(action(newImage => {
          if (this.originalImage) {
            if (this.image) { window.URL.revokeObjectURL(this.image); }
          } else {
            this.originalImage = this.image;
          }

          this.image = newImage;
          this.crop = null;
          this.rotation = newRotation;
          this.isRotating = false;
        }))
        .catch(action(() => {
          this.isRotating = false;
        }))
        .done();
    }
  }

  @action finishCrop() {
    this.isSaving = true;
    this._createCanvasFromCrop()
      .then(canvas => {
        // If we don't need to resize then don't!
        if (!this._output && canvas.width <= this._maxOutputSize && canvas.height <= this._maxOutputSize) { return canvas; }

        const newCanvas = document.createElement('canvas');
        if (this._output) {
          let { width, height } = this._output;
          // We might need to calculate the other width/height based on the ratio
          if (!height) { height = Math.round(width * canvas.height / canvas.width); }
          if (!width) { width = Math.round(height * canvas.width / canvas.height); }
          newCanvas.width = width;
          newCanvas.height = height;
        } else {
          const ratio = this._maxOutputSize / (canvas.width >= canvas.height ? canvas.width : canvas.height);
          newCanvas.width = Math.round(canvas.width * ratio);
          newCanvas.height = Math.round(canvas.height * ratio);
        }
        return pica.resize(canvas, newCanvas, this._picaOptions);
      })
      .then(canvas => pica.toBlob(canvas, 'image/png'))
      .then(blob => this._onImageBlob && this._onImageBlob(blob))
      .then(() => this.cancel())
      .catch(log.catchAndNotify)
      .finally(action(() => (this.isSaving = false)))
      .done();
  }

  @action cropChange(crop) {
    if (this._maxAspect || this._minAspect) {
      const test = crop.width / crop.height;
      const { height } = this._imageDom;

      // Work out which aspect we need to fit to
      let newAspect, adjFunc;
      if (this._maxAspect && test > this._maxAspect) { newAspect = this._maxAspect; adjFunc = Math.ceil; }
      if (this._minAspect && test < this._minAspect) { newAspect = this._minAspect; adjFunc = Math.floor; }

      // If we have an aspect, then fit to that new aspect
      // Note: also need to make sure we do not go out of bounds of image
      if (newAspect) {
        let newWidth = crop.width;
        let newHeight = adjFunc(newWidth / newAspect);
        if (newHeight > height) {
          newHeight = height;
          newWidth = height * newAspect;
        }
        let y = crop.y;
        if ((y + newHeight) > height) {
          y = height - newHeight;
        }
        crop = { ...crop, width: newWidth, height: newHeight, y };
      }
    }
    this.crop = crop;
  }

  @action imageLoaded(image, aspect) {
    this._imageDom = image;

    const { width, height } = image;

    if (!aspect) {
      const test = width / height;
      if (this._maxAspect && test > this._maxAspect) { aspect = this._maxAspect; }
      if (this._minAspect && test < this._minAspect) { aspect = this._minAspect; }
    }

    this.crop = aspect ?
      centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 100
          },
          aspect,
          width,
          height
        ),
        width,
        height
      )
      :
      {
        unit: 'px',
        width,
        height,
        x: 0,
        y: 0
      };
  }

  @action cancel() {
    this._loadingId++;
    if (this._uploadResolve) {
      this._uploadResolve();
      this._uploadResolve = null;
    }
    if (this.image) {
      window.URL.revokeObjectURL(this.image);
      this.image = null;
    }
    if (this.originalImage) {
      window.URL.revokeObjectURL(this.originalImage);
      this.originalImage = null;
    }
    this._imageDom = null;
    this.crop = null;
    this.isLoadingImage = false;
    this._onShowEditor && this._onShowEditor(false);
  }

  _loadImage(file, orientation) {
    const filePromise = this._isHeic(file) ? this._loadHeicImage(file) : Promise.resolve(file);

    return filePromise
      .then(f => loadImage(f, {
        orientation,
        canvas: true
      }))
      .then(res => pica.toBlob(res.image, 'image/png'))
      .then(blob => window.URL.createObjectURL(blob))
      .catch(err => {
        log.catchAndNotify(err);
        throw err;
      });
  }

  _isHeic(file) {
    const ext = (file.name || '').split('.').pop();
    return file.type === 'image/heic' || file.type === 'image/heif' || ext === 'heic' || ext === 'heif';
  }

  async _loadHeicImage(file) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const config = {
      headers: { 'content-type': 'multipart/form-data' },
      timeout: 0, // Uploads can take a while! Have no timeout...
      responseType: 'blob' // We want a blob for the load image func later
    };
    const res = await api.post('files/convert/heic', formData, config);
    return res.data;
  }

  _createCanvasFromCrop() {
    const { crop, image } = this;

    // Annoyingly we need to load an image, and then draw it to the canvas...
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');

        const { width: imageWidth, height: imageHeight, naturalWidth, naturalHeight } = this._imageDom;
        const scaleX = naturalWidth / imageWidth;
        const scaleY = naturalHeight / imageHeight;

        let { width: cropWidth, height: cropHeight, x: cropX, y: cropY, unit } = crop;

        // Might need to convert from %
        if (unit === '%') {
          cropWidth = cropWidth * imageWidth / 100;
          cropHeight = cropHeight * imageHeight / 100;
          cropX = cropX * imageWidth / 100;
          cropY = cropY * imageHeight / 100;
        }

        const width = Math.round(cropWidth * scaleX);
        const height = Math.round(cropHeight * scaleY);
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
          img,
          Math.round(cropX * scaleX),
          Math.round(cropY * scaleY),
          width,
          height,
          0,
          0,
          width,
          height
        );

        resolve(canvas);
      };
      img.onerror = reject;
      img.src = image;
    });
  }
}